import React from "react";
import { Heading, HeadingProps, Icon, Button } from "@telia/teddy";
import { HeadingVariant } from "@telia/teddy/dist/components/heading/heading";
import cn from "classnames";
import _filter from "lodash/filter";
import _isEmpty from "lodash/isEmpty";
import { useSelector } from "react-redux";
import { TextBlock, TextBlockUsp } from "../../../../../../models";
import { isUspVisibleInFlow } from "../../../../../../shared/uspFlow";
import { isRetailer } from "../../../../app/state/selectors";
import { useViewport } from "../../../../checkout/hooks";
import "./SubscriptionTextBlock.less";

export const SUBSCRIPTION_TEXTBLOCK_BREAKPOINT = 1200;
const THEME_TEXT_COLOR = "var(--teddy-theme-color-text-default)";
export const USE_ALTERNATIVE_STYLES = "Use alternative styles";

type HeadingSize = "xs" | "s" | "m" | "l" | "xl";
interface SusbcriptionTextBlockProps {
    className?: string;
    doubleSpeed?: string;
    headingSize?: HeadingSize;
    headingTag?: HeadingProps["AsProps"]["as"];
    setModal?: (modalId: string) => void;
    shouldCenterOnDesktop?: boolean;
    showExistingGetOffer?: boolean;
    subscriptionSpeed?: {
        amount: number;
        unit: string;
    };
    textBlock: TextBlock;
}

interface UpdatedDescriptionProps {
    description?: string;
    doubleSpeed?: string | undefined;
    subscriptionSpeed?: { amount: number; unit: string } | undefined;
}

const getUpdatedDescription = ({
    subscriptionSpeed,
    doubleSpeed,
    description = "",
}: UpdatedDescriptionProps): string => {
    const regex = /\[\$\$\$.*\]/gm;
    const match = regex.exec(description);

    if (match && match.length > 0) {
        const firstMatch = match[0] || "";
        const [, type, id, text] = firstMatch.substring(1, firstMatch.length - 1).split(":");

        switch (type) {
            case "MODAL":
                return description.replace(match[0], `<a data-modal-id="${id}" class="link" href="#">${text}</a>`);
            default:
        }
    }

    if (description.includes("[SPEED]")) {
        if (doubleSpeed) {
            return description.replace(
                "[SPEED]",
                `<span class="subscriptionSpeedStrikethrough">${subscriptionSpeed?.amount} ${subscriptionSpeed?.unit}</span>
          <span>${doubleSpeed} ${subscriptionSpeed?.unit}</span>
        `,
            );
        }
        return description.replace("[SPEED]", `${subscriptionSpeed?.amount} ${subscriptionSpeed?.unit}`);
    }

    return description;
};

const SubscriptionTextBlockUsp: React.FC<{
    doubleSpeed?: string;
    setModal?: (modalId: string) => void;
    subscriptionSpeed?: {
        amount: number;
        unit: string;
    };
    textBlock: TextBlock;
    usp: TextBlockUsp;
}> = ({ textBlock, usp, setModal, subscriptionSpeed, doubleSpeed }) => {
    const description = getUpdatedDescription({
        subscriptionSpeed,
        doubleSpeed,
        description: usp.title || usp.heading,
    });

    const clickHandler = (e: React.MouseEvent<HTMLElement>): void => {
        const el = (e.target as HTMLElement).closest("a");
        if (el && e.currentTarget.contains(el)) {
            const modalId = el.getAttribute("data-modal-id");
            if (setModal && modalId) {
                setModal(modalId);
            }
        }
    };

    return (
        <div
            key={`${textBlock.heading}-usp-${usp.heading}`}
            className={cn("subscription-text-block__usp-item", {
                "subscription-text-block__usp-item--alternative": usp.presentationStyle === "alternative",
            })}
        >
            {usp.icon && (
                <span>
                    <Icon
                        className={"subscription-text-block__usp-icon"}
                        name={usp.icon}
                        aria-hidden="true"
                        focusable="false"
                    />
                </span>
            )}
            {description && (
                <div className="subscription-text-block__usp-content" onClick={clickHandler}>
                    <div dangerouslySetInnerHTML={{ __html: description }} />
                </div>
            )}
        </div>
    );
};

const sizeMap: Record<HeadingSize, HeadingVariant> = {
    xs: "title-100",
    s: "title-200",
    m: "title-300",
    l: "title-400",
    xl: "title-500",
};

const SubscriptionTextBlock: React.FC<SusbcriptionTextBlockProps> = ({
    textBlock,
    headingSize,
    headingTag,
    showExistingGetOffer,
    subscriptionSpeed,
    doubleSpeed,
    className = "",
    shouldCenterOnDesktop = false,
    setModal,
}: SusbcriptionTextBlockProps) => {
    const isUspGridLayout = textBlock.uspLayout === "grid";
    const isUspRowLayout = textBlock.uspLayout === "row";
    const { width } = useViewport();
    const isNexus = useSelector(isRetailer);
    const content = (showExistingGetOffer && textBlock.contentTeliaSamlet) || textBlock.content;
    const heading = (showExistingGetOffer && textBlock.headingTeliaSamlet) || textBlock.heading;
    const button = (showExistingGetOffer && textBlock.buttonTeliaSamlet) || textBlock.button;
    const shouldShowUsp = (usp: TextBlockUsp) => usp.heading !== "DUMMY" && isUspVisibleInFlow(usp.flow, isNexus);
    const uspListFiltered = _filter(textBlock.uspList, shouldShowUsp);
    const uspListTeliaSamletFiltered = _filter(textBlock.uspListTeliaSamlet, shouldShowUsp);
    const uspList =
        showExistingGetOffer && !_isEmpty(uspListTeliaSamletFiltered) ? uspListTeliaSamletFiltered : uspListFiltered;
    // A CMS background has no theme-aware counterpart, so keep the fixed dark text on top of it
    const hasCmsBackground = !!textBlock.contentBackgroundColor;
    const headingFallbackColor = hasCmsBackground ? "black" : THEME_TEXT_COLOR;
    const contentFallbackColor = hasCmsBackground ? "#222" : THEME_TEXT_COLOR;

    return (
        <div
            className={cn("subscription-text-block", {
                "subscription-text-block__desktop-center": shouldCenterOnDesktop,
                "subscription-text-block__background-color": !!textBlock.contentBackgroundColor,
                "subscription-text-block__background-border": !!textBlock.shouldShowBorder,
                [className]: className,
            })}
            style={{ background: textBlock.contentBackgroundColor || "none" }}
            data-cy="sub-text-block"
        >
            {heading && (
                <div className={"subscription-text-block__heading-container"}>
                    {textBlock.headingIcon && (
                        <Icon
                            className={"subscription-text-block__heading-icon"}
                            name={textBlock.headingIcon}
                            aria-hidden="true"
                            focusable="false"
                        />
                    )}

                    <Heading
                        mb="100"
                        as={headingTag || "h2"}
                        variant={sizeMap[headingSize || "s"]}
                        className={cn({ ["flex-self-start"]: true, ["subscription-text-block__heading"]: true })}
                        style={{ color: textBlock.headingTextColor || headingFallbackColor }}
                    >
                        <div dangerouslySetInnerHTML={{ __html: heading }} />
                    </Heading>
                </div>
            )}
            {content && (
                <div
                    className={"flex-self-start"}
                    dangerouslySetInnerHTML={{
                        __html: content,
                    }}
                    style={{
                        color:
                            width < SUBSCRIPTION_TEXTBLOCK_BREAKPOINT
                                ? textBlock.contentTextColorMobile
                                : textBlock.contentTextColor || contentFallbackColor,
                    }}
                />
            )}
            {!_isEmpty(uspList) && (
                <div
                    className={cn("subscription-text-block__usps", {
                        "subscription-text-block__usps__grid": isUspGridLayout,
                        "subscription-text-block__usps__row": isUspRowLayout,
                    })}
                    style={{ color: textBlock.contentTextColor || contentFallbackColor }}
                >
                    {uspList.map((usp) => (
                        <SubscriptionTextBlockUsp
                            textBlock={textBlock}
                            usp={usp}
                            subscriptionSpeed={subscriptionSpeed}
                            doubleSpeed={doubleSpeed}
                            setModal={setModal}
                            key={`${textBlock.heading}-usp-${usp.heading || usp.icon}`}
                        />
                    ))}
                </div>
            )}
            {button && button.alternative_id !== "DUMMY" && (
                <Button variant={button.kind}>
                    <span>{button.text}</span>
                </Button>
            )}
        </div>
    );
};

export default SubscriptionTextBlock;
